import { useState, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { X, Globe, Clock, Plus, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const API_BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings';

const PAGINATED_BOOKINGS_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings/viewAllBoookings';

const RESOURCE_BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150';

const getAccessToken = () => sessionStorage.getItem('access_token');

function parseDuration(duration) {
  if (!duration) return 60;
  const match = duration.match(/PT(\d+H)?(\d+M)?/);
  if (!match) return 60;
  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  return hours * 60 + minutes;
}

function getHongKongTime() {
  return new Date(
    new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Jakarta'
    })
  );
}

function isTimePast(dateString, timeString) {
  const slotDateTime = new Date(
    `${dateString}T${timeString}:00`
  );

  return slotDateTime <= new Date();
}

const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${hours12}:${minutes
    .toString()
    .padStart(2, '0')} ${period}`;
};

const calculateDuration = (startTime, endTime) => {
  const [startHours, startMins] = startTime
    .split(':')
    .map(Number);

  const [endHours, endMins] = endTime
    .split(':')
    .map(Number);

  const startTotalMins =
    startHours * 60 + startMins;

  const endTotalMins =
    endHours * 60 + endMins;

  return endTotalMins - startTotalMins;
};

const addMinutesToTime = (
  timeString,
  minutes
) => {
  const [hours, mins] = timeString
    .split(':')
    .map(Number);

  const totalMinutes =
    hours * 60 + mins + minutes;

  const newHours =
    Math.floor(totalMinutes / 60) % 24;

  const newMins =
    totalMinutes % 60;

  return `${newHours
    .toString()
    .padStart(2, '0')}:${newMins
    .toString()
    .padStart(2, '0')}`;
};

const calculatePrice = (
  startDate,
  endDate,
  startTime,
  endTime,
  rates
) => {
  if (
    !startDate ||
    !endDate ||
    !startTime ||
    !endTime ||
    !rates ||
    rates.length === 0
  ) {
    return '0';
  }

  try {
    const start = new Date(
      `${startDate}T${startTime}:00`
    );

    const end = new Date(
      `${endDate}T${endTime}:00`
    );

    const totalMinutes =
      (end - start) / (1000 * 60);

    if (totalMinutes <= 0) return '0';

    const totalHours = totalMinutes / 60;
    const totalDays =
      totalMinutes / (60 * 24);

    let selectedRate = null;
    let calculatedPrice = 0;

    if (totalDays >= 1) {
      selectedRate = rates.find(
        (r) =>
          r.renewal_type === 'every day'
      );

      if (selectedRate) {
        calculatedPrice =
          selectedRate.amount *
          Math.ceil(totalDays);
      }
    }

    if (!selectedRate) {
      selectedRate = rates.find(
        (r) =>
          r.renewal_type === 'every hour'
      );

      if (selectedRate) {
        calculatedPrice =
          selectedRate.amount *
          Math.ceil(totalHours);
      }
    }

    if (!selectedRate && rates.length > 0) {
      selectedRate = rates[0];

      if (
        selectedRate.renewal_type ===
        'every use'
      ) {
        calculatedPrice =
          selectedRate.amount;
      } else {
        calculatedPrice =
          selectedRate.amount *
          Math.ceil(totalHours);
      }
    }

    return calculatedPrice.toFixed(3);
  } catch (error) {
    console.error(
      'Error calculating price:',
      error
    );

    return '0';
  }
};

const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return mins > 0
    ? `${hours}h ${mins}m`
    : `${hours}h`;
};

const getTimezoneOffset = () => {
  const offsetMinutes =
    new Date().getTimezoneOffset();

  const sign =
    offsetMinutes <= 0 ? '+' : '-';

  const abs = Math.abs(offsetMinutes);

  const h = String(
    Math.floor(abs / 60)
  ).padStart(2, '0');

  const m = String(
    abs % 60
  ).padStart(2, '0');

  return `${sign}${h}:${m}`;
};

const parseBookingError = (
  errorData,
  serviceDetails,
  formData
) => {
  const errorMessage =
    errorData?.error ||
    errorData?.message ||
    errorData?.details ||
    '';

  const errorLower =
    errorMessage.toLowerCase();

  if (
    errorData?.errors &&
    typeof errorData.errors === 'object'
  ) {
    const fieldErrors =
      Object.entries(errorData.errors)
        .map(([field, messages]) => {
          const msgArray =
            Array.isArray(messages)
              ? messages
              : [messages];

          return `${field}: ${msgArray.join(
            ', '
          )}`;
        })
        .join('; ');

    if (fieldErrors) {
      return `Validation error: ${fieldErrors}`;
    }
  }

  if (
    errorLower.includes('duration') ||
    errorLower.includes('min') ||
    errorLower.includes('max')
  ) {
    if (serviceDetails) {
      const minDur =
        serviceDetails.min_duration
          ? parseDuration(
              serviceDetails.min_duration
            )
          : null;

      const maxDur =
        serviceDetails.max_duration
          ? parseDuration(
              serviceDetails.max_duration
            )
          : null;

      if (minDur && maxDur) {
        return `Booking duration must be between ${formatDuration(
          minDur
        )} and ${formatDuration(
          maxDur
        )}. Please adjust your start and end times.`;
      }

      if (minDur) {
        return `Booking duration must be at least ${formatDuration(
          minDur
        )}. Please extend your booking time.`;
      }

      if (maxDur) {
        return `Booking duration cannot exceed ${formatDuration(
          maxDur
        )}. Please shorten your booking time.`;
      }
    }

    return 'The selected booking duration is not allowed for this resource. Please check the minimum and maximum duration limits.';
  }

  if (
    errorLower.includes('schedule') ||
    errorLower.includes('available') ||
    errorLower.includes('time slot') ||
    errorLower.includes('not available')
  ) {
    return 'This resource is not available at the selected time. Please check the resource schedule and choose a different time slot.';
  }

  if (
    errorLower.includes('conflict') ||
    errorLower.includes('already booked') ||
    errorLower.includes('overlap')
  ) {
    return 'This time slot conflicts with an existing booking. Please select a different time or check the calendar for availability.';
  }

  if (
    errorLower.includes('resource') &&
    (errorLower.includes('not found') ||
      errorLower.includes('invalid'))
  ) {
    return 'The selected resource is not available. Please refresh the page and try again.';
  }

  return errorMessage;
};

export default function BookingSidePanel({
  isOpen,
  onClose,
  booking = null,
  selectedDate = null,
  selectedTimeSlot = null,
  onSave,
  onDelete,
  onNewBooking,
  isCollapsed = false,
  onToggleCollapse
}) {
  const [formData, setFormData] =
    useState({
      customer_name: '',
      customer_id: '',
      resource_id: '',
      service_id: '',
      location_id:
        '80c10e0e-3ead-4740-a2a6-574354bb9a77',
      start_time: '12:00',
      end_time: '13:00',
      start_date: new Date()
        .toISOString()
        .split('T')[0],
      end_date: new Date()
        .toISOString()
        .split('T')[0],
      all_day: false,
      notes: '',
      price: '0.000',
      mongo_id: ''
    });

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [
    loadingResourceData,
    setLoadingResourceData
  ] = useState(false);

  const [success, setSuccess] =
    useState(false);

  const [
    showDeleteConfirmModal,
    setShowDeleteConfirmModal
  ] = useState(false);

  const [resources, setResources] =
    useState([]);

  const [locations, setLocations] =
    useState([]);

  const [customers, setCustomers] =
    useState([]);

  const [
    selectedDuration,
    setSelectedDuration
  ] = useState(60);

  const [
    availableSlots,
    setAvailableSlots
  ] = useState([]);

  const [
    serviceDetails,
    setServiceDetails
  ] = useState(null);

  const [
    scheduleBlocks,
    setScheduleBlocks
  ] = useState([]);

  const [
    validDates,
    setValidDates
  ] = useState([]);

  const [
    resourceDetails,
    setResourceDetails
  ] = useState(null);

  const [
    loadingSlots,
    setLoadingSlots
  ] = useState(false);

  const [
    isPastBooking,
    setIsPastBooking
  ] = useState(false);

  const [
    actionLoading,
    setActionLoading
  ] = useState(null);

  const [
    showCancelConfirmModal,
    setShowCancelConfirmModal
  ] = useState(false);

  const [
    loggedInUser,
    setLoggedInUser
  ] = useState(null);

  const [
    isCustomerDropdownOpen,
    setIsCustomerDropdownOpen
  ] = useState(false);

  const [
    customerSearchQuery,
    setCustomerSearchQuery
  ] = useState('');

  const bookingIsPast = useMemo(() => {
    if (!booking?.starts_at) {
      return false;
    }

    const tz =
      booking.location?.time_zone ||
      'Asia/Jakarta';

    const end = booking.ends_at
      ? DateTime.fromISO(
          booking.ends_at
        ).setZone(tz)
      : DateTime.fromISO(
          booking.starts_at
        ).setZone(tz);

    const now =
      DateTime.now().setZone(tz);

    return end < now;
  }, [booking]);

  useEffect(() => {
    const storedUser = JSON.parse(
      sessionStorage.getItem('user') ||
        '{}'
    );

    if (storedUser?.email) {
      setLoggedInUser(storedUser);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccess(false);
      loadInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    const loadBookingData = async () => {
      if (booking) {
        const timezone =
          booking.location?.time_zone ||
          'Asia/Jakarta';

        const startDateTime =
          booking.starts_at
            ? DateTime.fromISO(
                booking.starts_at
              ).setZone(timezone)
            : null;

        const endDateTime =
          booking.ends_at
            ? DateTime.fromISO(
                booking.ends_at
              ).setZone(timezone)
            : startDateTime;

        if (startDateTime) {
          const startTime =
            startDateTime.toFormat(
              'HH:mm'
            );

          const endTime =
            endDateTime?.toFormat(
              'HH:mm'
            ) || startTime;

          const bookingCustomerEmail =
            booking.customer_id ||
            booking.customer_name;

          const matchedCustomer =
            customers.find(
              (c) =>
                c.email ===
                  bookingCustomerEmail ||
                c.id ===
                  booking.customer_id ||
                c.name ===
                  booking.customer_name
            );

          const mongoId =
            booking.mongo_id ||
            booking._id ||
            booking.mongoId;

          let notesFromMongo = '';

          if (mongoId) {
            try {
              const response =
                await fetch(
                  `${API_BASE_URL}/booking/${mongoId}`,
                  {
                    method: 'GET',
                    headers: {
                      'Content-Type':
                        'application/json',
                      Authorization: `Bearer ${getAccessToken()}`
                    }
                  }
                );

              if (response.ok) {
                const mongoData =
                  await response.json();

                notesFromMongo =
                  mongoData.notes || '';
              }
            } catch (error) {
              console.error(
                'Error fetching notes from MongoDB:',
                error
              );
            }
          }

          setFormData({
            customer_name:
              booking.customer_name ||
              booking.customer ||
              '',
            customer_id:
              matchedCustomer?.id ||
              booking.customer_id ||
              '',
            resource_id:
              booking.resource?.id ||
              '',
            service_id:
              booking.service?.id ||
              '',
            location_id:
              '80c10e0e-3ead-4740-a2a6-574354bb9a77',
            start_time: startTime,
            end_time: endTime,
            start_date:
              startDateTime.toFormat(
                'yyyy-LL-dd'
              ),
            end_date:
              endDateTime.toFormat(
                'yyyy-LL-dd'
              ),
            all_day: false,
            notes:
              notesFromMongo ||
              booking.notes ||
              booking.metadata?.notes ||
              '',
            price:
              booking.price || '0',
            mongo_id: mongoId
          });

          const duration =
            calculateDuration(
              startTime,
              endTime
            );

          setSelectedDuration(
            duration
          );
        }
      } else if (
        selectedDate ||
        selectedTimeSlot
      ) {
        let dateToUse = new Date();

        if (selectedDate) {
          dateToUse =
            selectedDate instanceof Date
              ? selectedDate
              : new Date(selectedDate);
        }

        const year =
          dateToUse.getFullYear();

        const month = String(
          dateToUse.getMonth() + 1
        ).padStart(2, '0');

        const day = String(
          dateToUse.getDate()
        ).padStart(2, '0');

        const formattedDate = `${year}-${month}-${day}`;

        const timeToUse =
          selectedTimeSlot ||
          formData.start_time ||
          '12:00';

        const defaultDuration =
          selectedDuration || 60;

        const endTime =
          addMinutesToTime(
            timeToUse,
            defaultDuration
          );

        setFormData((prev) => ({
          ...prev,
          start_time: timeToUse,
          end_time: endTime,
          start_date: formattedDate,
          end_date: formattedDate
        }));

        if (!selectedDuration) {
          setSelectedDuration(
            defaultDuration
          );
        }
      }
    };

    loadBookingData();
  }, [
    booking,
    selectedTimeSlot,
    selectedDate,
    customers
  ]);

  const loadInitialData = async () => {
    try {
      setFormData((prev) => ({
        ...prev,
        location_id:
          '80c10e0e-3ead-4740-a2a6-574354bb9a77'
      }));

      const resourcesRes =
        await fetch(
          `${RESOURCE_BASE_URL}/resource/viewAllResource`,
          {
            headers: {
              'Content-Type':
                'application/json',
              Authorization: `Bearer ${getAccessToken()}`
            }
          }
        );

      if (resourcesRes.ok) {
        const resourcesData =
          await resourcesRes.json();

        const resourcesList =
          Array.isArray(resourcesData)
            ? resourcesData
            : resourcesData.data ||
              resourcesData.resources ||
              [];

        setResources(resourcesList);
      }

      await loadCustomers();
    } catch (err) {
      setError(
        'Failed to load form data'
      );

      console.error(
        'Error loading initial data:',
        err
      );
    }
  };

  const loadCustomers = async () => {
    try {
      const response =
        await fetch(
          `${RESOURCE_BASE_URL}/customer/getAllCustomer`,
          {
            method: 'GET',
            headers: {
              'Content-Type':
                'application/json',
              Authorization: `Bearer ${getAccessToken()}`
            }
          }
        );

      if (!response.ok) {
        console.error(
          'Failed to fetch customers'
        );

        return;
      }

      const data =
        await response.json();

      const customersData =
        (
          data.customers || []
        ).map((customer) => ({
          id: customer._id,
          name: customer.fullName,
          email: customer.email
        }));

      setCustomers(customersData);
    } catch (error) {
      console.error(
        'Error loading customers:',
        error
      );
    }
  };

  useEffect(() => {
    const loadResourceData =
      async () => {
        if (!formData.resource_id) {
          setFormData((prev) => ({
            ...prev,
            service_id: '',
            price: '0'
          }));

          setServiceDetails(null);
          setScheduleBlocks([]);
          setValidDates([]);
          setResourceDetails(null);

          return;
        }

        try {
          setLoadingResourceData(true);

          const selectedResource =
            resources.find(
              (r) =>
                r.id ===
                formData.resource_id
            );

          if (!selectedResource) {
            return;
          }

          const resourceRes =
            await fetch(
              `${RESOURCE_BASE_URL}/resource/getResourceByName/${encodeURIComponent(
                selectedResource.name
              )}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type':
                    'application/json',
                  Authorization: `Bearer ${getAccessToken()}`
                }
              }
            );

          if (resourceRes.ok) {
            const resourceData =
              await resourceRes.json();

            const resource =
              resourceData.resource;

            if (resource) {
              setResourceDetails(
                resource
              );

              const rates =
                resource.metadata
                  ?.rates || [];

              if (
                rates.length > 0 &&
                rates[0].amount !==
                  undefined
              ) {
                const price =
                  parseFloat(
                    rates[0].amount
                  ).toFixed(3);

                setFormData((prev) => ({
                  ...prev,
                  price
                }));
              }
            }
          }

          const serviceIdRes =
            await fetch(
              `${RESOURCE_BASE_URL}/service/getServiceIdByResourceId/${formData.resource_id}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type':
                    'application/json',
                  Authorization: `Bearer ${getAccessToken()}`
                }
              }
            );

          if (!serviceIdRes.ok) {
            const errorText =
              await serviceIdRes.text();

            console.error(
              'Error response:',
              errorText
            );

            throw new Error(
              `Failed to get service ID: ${serviceIdRes.status}`
            );
          }

          const serviceData =
            await serviceIdRes.json();

          const serviceId =
            serviceData.serviceId;

          if (!serviceId) {
            throw new Error(
              'Service ID is missing from API response'
            );
          }

          setFormData((prev) => ({
            ...prev,
            service_id: serviceId
          }));

          const serviceRes =
            await fetch(
              `${RESOURCE_BASE_URL}/service/viewServiceByServiceId/${serviceId}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type':
                    'application/json',
                  Authorization: `Bearer ${getAccessToken()}`
                }
              }
            );

          if (serviceRes.ok) {
            const service =
              await serviceRes.json();

            setServiceDetails(
              service
            );

            if (service.duration) {
              const durationMinutes =
                parseDuration(
                  service.duration
                );

              const endTime =
                addMinutesToTime(
                  formData.start_time,
                  durationMinutes
                );

              setFormData((prev) => ({
                ...prev,
                end_time: endTime
              }));

              setSelectedDuration(
                durationMinutes
              );
            }
          }

          const scheduleRes =
            await fetch(
              `${RESOURCE_BASE_URL}/resource/getResourceScheduleInfo/${encodeURIComponent(
                selectedResource.name
              )}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type':
                    'application/json',
                  Authorization: `Bearer ${getAccessToken()}`
                }
              }
            );

          if (scheduleRes.ok) {
            const scheduleData =
              await scheduleRes.json();

            const blocks =
              scheduleData.data
                ?.schedule_blocks ||
              [];

            const recurringSchedule =
              scheduleData.data
                ?.recurring_schedule ||
              {};

            setScheduleBlocks(
              blocks
            );

            calculateValidDates(
              blocks,
              recurringSchedule
            );
          }
        } catch (error) {
          console.error(
            'Error loading resource data:',
            error
          );
        } finally {
          setLoadingResourceData(
            false
          );
        }
      };

    loadResourceData();
  }, [
    formData.resource_id,
    resources
  ]);

  const calculateValidDates = (
    blocks,
    recurringSchedule
  ) => {
    if (
      !blocks ||
      blocks.length === 0
    ) {
      setValidDates([]);
      return;
    }

    const startDate =
      recurringSchedule?.start_date
        ? new Date(
            recurringSchedule.start_date
          )
        : new Date();

    const endDate =
      recurringSchedule?.end_date
        ? new Date(
            recurringSchedule.end_date
          )
        : new Date(
            Date.now() +
              365 *
                24 *
                60 *
                60 *
                1000
          );

    const validDays = new Set(
      blocks.map((block) =>
        block.weekday.toLowerCase()
      )
    );

    const dates = [];

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dayName =
        d
          .toLocaleDateString(
            'en-US',
            {
              weekday: 'long'
            }
          )
          .toLowerCase();

      if (validDays.has(dayName)) {
        dates.push(
          new Date(d)
            .toISOString()
            .split('T')[0]
        );
      }
    }

    setValidDates(dates);
  };

  const fetchAllPaginatedBookings =
    async () => {
      const allBookings = [];

      const limit = 100;

      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const response =
          await fetch(
            `${PAGINATED_BOOKINGS_URL}?page=${page}&limit=${limit}`,
            {
              method: 'GET',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch bookings: ${response.status}`
          );
        }

        const result =
          await response.json();

        const pageBookings =
          Array.isArray(result)
            ? result
            : result.data || [];

        allBookings.push(
          ...pageBookings
        );

        const total =
          result.meta?.total ??
          result.meta?.total_count ??
          result.meta?.count ??
          result.total ??
          result.total_count;

        totalPages =
          result.meta?.totalPages ??
          result.meta?.total_pages ??
          result.totalPages ??
          result.total_pages ??
          (total
            ? Math.ceil(
                total / limit
              )
            : pageBookings.length <
              limit
            ? page
            : page + 1);

        if (
          pageBookings.length === 0
        ) {
          break;
        }

        page += 1;
      }

      return allBookings;
    };

  useEffect(() => {
    const loadAvailableSlots =
      async () => {
        if (
          !formData.resource_id ||
          !formData.start_date ||
          !formData.service_id
        ) {
          setAvailableSlots([]);
          return;
        }

        try {
          setLoadingSlots(true);

          const allBookings =
            await fetchAllPaginatedBookings();

          const resourceBookings =
            allBookings.filter(
              (booking) =>
                booking.resource?.id ===
                formData.resource_id
            );

          const dayBookings =
            resourceBookings.filter(
              (booking) => {
                if (!booking.starts_at) {
                  return false;
                }

                const bookingDateTime =
                  new Date(
                    booking.starts_at
                  );

                const bookingDateStr =
                  bookingDateTime.toLocaleDateString(
                    'en-CA'
                  );

                return (
                  bookingDateStr ===
                    formData.start_date &&
                  !booking.is_canceled
                );
              }
            );

          const selectedDate =
            new Date(
              formData.start_date
            );

          const weekday =
            selectedDate
              .toLocaleDateString(
                'en-US',
                {
                  weekday: 'long'
                }
              )
              .toLowerCase();

          const dayBlocks =
            scheduleBlocks.filter(
              (block) =>
                block.weekday ===
                weekday
            );

          const interval =
            serviceDetails?.bookable_interval
              ? parseDuration(
                  serviceDetails.bookable_interval
                )
              : serviceDetails?.duration_step
                ? parseDuration(
                    serviceDetails.duration_step
                  )
                : 15;

          const slots = [];

          dayBlocks.forEach(
            (block) => {
              const [
                startHour,
                startMin
              ] = block.start_time
                .split(':')
                .map(Number);

              const [
                endHour,
                endMin
              ] = block.end_time
                .split(':')
                .map(Number);

              const blockStart =
                startHour * 60 +
                startMin;

              const blockEnd =
                endHour * 60 +
                endMin;

              for (
                let time = blockStart;
                time < blockEnd;
                time += interval
              ) {
                const hour =
                  Math.floor(
                    time / 60
                  );

                const minute =
                  time % 60;

                const timeStr = `${hour
                  .toString()
                  .padStart(
                    2,
                    '0'
                  )}:${minute
                  .toString()
                  .padStart(
                    2,
                    '0'
                  )}`;

                const isBooked =
                  dayBookings.some(
                    (booking) => {
                      const bookingStart =
                        new Date(
                          booking.starts_at
                        );

                      const bookingEnd =
                        new Date(
                          booking.ends_at
                        );

                      const slotStartTime =
                        new Date(
                          `${formData.start_date}T${timeStr}:00+08:00`
                        );

                      const slotDuration =
                        serviceDetails?.duration
                          ? parseDuration(
                              serviceDetails.duration
                            )
                          : interval;

                      const slotEndTime =
                        new Date(
                          slotStartTime.getTime() +
                            slotDuration *
                              60 *
                              1000
                        );

                      return (
                        slotStartTime <
                          bookingEnd &&
                        slotEndTime >
                          bookingStart
                      );
                    }
                  );

                slots.push({
                  time: timeStr,
                  available:
                    !isBooked
                });
              }
            }
          );

          setAvailableSlots(
            slots
          );
        } catch (error) {
          console.error(
            'Error loading available slots:',
            error
          );

          setAvailableSlots([]);
        } finally {
          setLoadingSlots(false);
        }
      };

    loadAvailableSlots();
  }, [
    formData.resource_id,
    formData.start_date,
    formData.service_id,
    scheduleBlocks,
    serviceDetails
  ]);

  useEffect(() => {
    if (
      availableSlots.length === 0 ||
      !formData.start_date
    ) {
      return;
    }

    const currentTimeIsPast =
      isTimePast(
        formData.start_date,
        formData.start_time
      );

    const currentSlot =
      availableSlots.find(
        (s) =>
          s.time ===
          formData.start_time
      );

    const currentTimeUnavailable =
      !currentSlot ||
      !currentSlot.available;

    if (
      currentTimeIsPast ||
      currentTimeUnavailable
    ) {
      const firstAvailable =
        availableSlots.find(
          (slot) =>
            slot.available &&
            !isTimePast(
              formData.start_date,
              slot.time
            )
        );

      if (firstAvailable) {
        setFormData((prev) => {
          const newStartTime =
            firstAvailable.time;

          let newEndTime =
            prev.end_time;

          if (
            serviceDetails?.duration
          ) {
            const durationMinutes =
              parseDuration(
                serviceDetails.duration
              );

            newEndTime =
              addMinutesToTime(
                newStartTime,
                durationMinutes
              );
          }

          return {
            ...prev,
            start_time:
              newStartTime,
            end_time:
              newEndTime
          };
        });
      }
    }
  }, [
    availableSlots,
    formData.start_date,
    serviceDetails
  ]);

  useEffect(() => {
    if (
      !formData.start_date ||
      !formData.start_time
    ) {
      return;
    }

    if (
      formData.end_time <=
      formData.start_time
    ) {
      if (
        serviceDetails?.duration
      ) {
        const durationMinutes =
          parseDuration(
            serviceDetails.duration
          );

        const calculatedEndTime =
          addMinutesToTime(
            formData.start_time,
            durationMinutes
          );

        setFormData((prev) => ({
          ...prev,
          end_time:
            calculatedEndTime
        }));
      }
    }
  }, [
    formData.start_time,
    formData.start_date,
    serviceDetails
  ]);

  useEffect(() => {
    if (
      !resourceDetails?.metadata
        ?.rates ||
      resourceDetails.metadata.rates
        .length === 0
    ) {
      return;
    }

    if (
      formData.start_date &&
      formData.end_date &&
      formData.start_time &&
      formData.end_time
    ) {
      const newPrice =
        calculatePrice(
          formData.start_date,
          formData.end_date,
          formData.start_time,
          formData.end_time,
          resourceDetails.metadata
            .rates
        );

      if (
        newPrice !==
        formData.price
      ) {
        setFormData((prev) => ({
          ...prev,
          price: newPrice
        }));
      }
    }
  }, [
    formData.start_date,
    formData.end_date,
    formData.start_time,
    formData.end_time,
    resourceDetails
  ]);

  const handleMarkTentative =
    async () => {
      if (!booking) return;

      if (bookingIsPast) {
        alert(
          'Cannot modify a booking that has already ended.'
        );

        return;
      }

      setActionLoading(
        'Mark Tentative'
      );

      try {
        const response =
          await fetch(
            `${API_BASE_URL}/updateBooking/${booking.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify({
                is_temporary: true
              })
            }
          );

        if (!response.ok) {
          throw new Error(
            'Failed to mark booking as tentative'
          );
        }

        setSuccess(true);

        setTimeout(() => {
          onSave && onSave();
        }, 1500);
      } catch (error) {
        console.error(
          'Error marking tentative:',
          error
        );

        setError(
          `Failed to mark booking as tentative: ${error.message}`
        );
      } finally {
        setActionLoading(null);
      }
    };

  const handleConfirmBooking =
    async () => {
      if (!booking) return;

      if (bookingIsPast) {
        alert(
          'Cannot modify a booking that has already ended.'
        );

        return;
      }

      setActionLoading(
        'Confirm'
      );

      try {
        const response =
          await fetch(
            `${API_BASE_URL}/updateBooking/${booking.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify({
                is_temporary: false
              })
            }
          );

        if (!response.ok) {
          throw new Error(
            'Failed to confirm booking'
          );
        }

        setSuccess(true);

        setTimeout(() => {
          onSave && onSave();
        }, 1500);
      } catch (error) {
        console.error(
          'Error confirming booking:',
          error
        );

        setError(
          `Failed to confirm booking: ${error.message}`
        );
      } finally {
        setActionLoading(null);
      }
    };

  const handleCancelBooking =
    async () => {
      if (!booking) return;

      if (bookingIsPast) {
        alert(
          'Cannot cancel a booking that has already ended.'
        );

        return;
      }

      setActionLoading('Cancel');

      try {
        const response =
          await fetch(
            `${API_BASE_URL}/updateBooking/${booking.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify({
                is_canceled: true
              })
            }
          );

        if (!response.ok) {
          throw new Error(
            'Failed to cancel booking'
          );
        }

        setSuccess(true);

        setTimeout(() => {
          onSave && onSave();
        }, 1500);
      } catch (error) {
        console.error(
          'Error cancelling booking:',
          error
        );

        setError(
          `Failed to cancel booking: ${error.message}`
        );
      } finally {
        setActionLoading(null);
        setShowCancelConfirmModal(
          false
        );
      }
    };

  const handleUpdateNotes =
    async (mongoId) => {
      if (!mongoId) {
        console.error(
          'No mongo_id available for updating notes'
        );

        return false;
      }

      try {
        const response =
          await fetch(
            `${API_BASE_URL}/booking/${mongoId}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify({
                notes:
                  formData.notes.trim()
              })
            }
          );

        if (!response.ok) {
          console.error(
            'Failed to update notes'
          );

          return false;
        }

        return true;
      } catch (error) {
        console.error(
          'Error updating notes:',
          error
        );

        return false;
      }
    };

  const handleSubmit = async (
    e
  ) => {
    e.preventDefault();

    setError('');
    setSuccess(false);

    if (
      booking &&
      bookingIsPast
    ) {
      setError(
        'Past bookings cannot be edited.'
      );

      return;
    }

    if (!formData.customer_id) {
      setError(
        'Please select a customer before saving.'
      );

      return;
    }

    if (
      serviceDetails &&
      !booking
    ) {
      const [
        startHour,
        startMin
      ] = formData.start_time
        .split(':')
        .map(Number);

      const [
        endHour,
        endMin
      ] = formData.end_time
        .split(':')
        .map(Number);

      const durationMinutes =
        endHour * 60 +
        endMin -
        (startHour * 60 +
          startMin);

      const minDuration =
        serviceDetails.min_duration
          ? parseDuration(
              serviceDetails.min_duration
            )
          : null;

      const maxDuration =
        serviceDetails.max_duration
          ? parseDuration(
              serviceDetails.max_duration
            )
          : null;

      if (
        minDuration &&
        durationMinutes <
          minDuration
      ) {
        setError(
          `Booking duration must be at least ${formatDuration(
            minDuration
          )}. Your booking is only ${formatDuration(
            durationMinutes
          )}. Please extend your end time.`
        );

        return;
      }

      if (
        maxDuration &&
        durationMinutes >
          maxDuration
      ) {
        setError(
          `Booking duration cannot exceed ${formatDuration(
            maxDuration
          )}. Your booking is ${formatDuration(
            durationMinutes
          )}. Please shorten your booking time.`
        );

        return;
      }
    }

    if (!booking) {
      const startSlot =
        availableSlots.find(
          (s) =>
            s.time ===
            formData.start_time
        );

      if (
        startSlot &&
        !startSlot.available
      ) {
        setError(
          'Selected start time is not available. This time slot is already booked or tentatively booked.'
        );

        return;
      }

      const endSlot =
        availableSlots.find(
          (s) =>
            s.time ===
            formData.end_time
        );

      if (
        endSlot &&
        !endSlot.available
      ) {
        setError(
          'Selected end time is not available. This time slot is already booked or tentatively booked.'
        );

        return;
      }

      const [
        startHour,
        startMin
      ] = formData.start_time
        .split(':')
        .map(Number);

      const [
        endHour,
        endMin
      ] = formData.end_time
        .split(':')
        .map(Number);

      const startTotalMins =
        startHour * 60 +
        startMin;

      const endTotalMins =
        endHour * 60 +
        endMin;

      for (
        let slot of availableSlots
      ) {
        const [
          slotHour,
          slotMin
        ] = slot.time
          .split(':')
          .map(Number);

        const slotTotalMins =
          slotHour * 60 +
          slotMin;

        if (
          slotTotalMins >=
            startTotalMins &&
          slotTotalMins <
            endTotalMins &&
          !slot.available
        ) {
          setError(
            'One or more time slots in your selected range are already booked or tentatively booked. Please select a different time.'
          );

          return;
        }
      }
    }

    setLoading(true);

    try {
      const selectedLocation =
        locations.find(
          (loc) =>
            loc.id ===
            formData.location_id
        ) || {
          id: '80c10e0e-3ead-4740-a2a6-574354bb9a77',
          name: 'Continnum',
          time_zone:
            'Asia/Jakarta'
        };

      const timezone =
        selectedLocation?.time_zone ||
        'Asia/Jakarta';

      const starts_at = `${formData.start_date}T${formData.start_time}:00+07:00`;

      const ends_at = `${formData.end_date}T${formData.end_time}:00+07:00`;

      const selectedCustomer =
        customers.find(
          (c) =>
            c.id ===
            formData.customer_id
        );

      let customerName =
        selectedCustomer?.name ||
        formData.customer_name.trim();

      if (
        customerName?.includes('@')
      ) {
        customerName =
          customerName
            .split('@')[0]
            .replace(/\./g, ' ')
            .split(' ')
            .map(
              (word) =>
                word
                  .charAt(0)
                  .toUpperCase() +
                word
                  .slice(1)
                  .toLowerCase()
            )
            .join(' ');
      }

      const customerEmail =
        selectedCustomer?.email ||
        formData.customer_name.trim();

      const bookingData = {
        resource_id:
          formData.resource_id,
        service_id:
          formData.service_id,
        location_id:
          formData.location_id,
        starts_at,
        ends_at,
        price:
          formData.price ||
          '0.000',
        customer_id:
          customerEmail,
        customer_name:
          customerName,
        notes:
          formData.notes.trim(),
        is_past_booking:
          isPastBooking
      };

      if (booking) {
        const response =
          await fetch(
            `${API_BASE_URL}/updateBooking/${booking.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify(
                bookingData
              )
            }
          );

        if (!response.ok) {
          const errorData =
            await response
              .json()
              .catch(() => ({
                message:
                  'Failed to update booking'
              }));

          const userMessage =
            parseBookingError(
              errorData,
              serviceDetails,
              formData
            );

          throw new Error(
            userMessage
          );
        }

        if (formData.mongo_id) {
          await handleUpdateNotes(
            formData.mongo_id
          );
        }

        setSuccess(true);

        setTimeout(() => {
          setSuccess(false);
        }, 3000);

        setTimeout(() => {
          onSave && onSave();
        }, 1500);
      } else {
        const response =
          await fetch(
            `${API_BASE_URL}/createBooking`,
            {
              method: 'POST',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify(
                bookingData
              )
            }
          );

        if (!response.ok) {
          const errorData =
            await response
              .json()
              .catch(() => ({
                message:
                  'Failed to create booking'
              }));

          const userMessage =
            parseBookingError(
              errorData,
              serviceDetails,
              formData
            );

          throw new Error(
            userMessage
          );
        }

        const responseData =
          await response.json();

        const newMongoId =
          responseData?.mongoId?._id ||
          responseData?._id;

        if (newMongoId) {
          setFormData((prev) => ({
            ...prev,
            mongo_id: newMongoId
          }));
        }

        setSuccess(true);

        setTimeout(() => {
          setSuccess(false);
        }, 3000);

        setTimeout(() => {
          onSave && onSave();
        }, 1500);
      }
    } catch (err) {
      setError(
        err.message ||
          'Failed to save booking'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!booking) return;

    setShowDeleteConfirmModal(
      true
    );
  };

  const confirmDelete =
    async () => {
      if (!booking) return;

      try {
        setLoading(true);

        setShowDeleteConfirmModal(
          false
        );

        const response =
          await fetch(
            `${API_BASE_URL}/deleteBooking/${booking.id}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            'Failed to delete booking'
          );
        }

        onDelete && onDelete();
      } catch (err) {
        setError(
          err.message ||
            'Failed to delete booking'
        );
      } finally {
        setLoading(false);
      }
    };

  const selectedLocation =
    locations.find(
      (loc) =>
        loc.id ===
        formData.location_id
    ) || {
      id: '80c10e0e-3ead-4740-a2a6-574354bb9a77',
      name: 'Continnum',
      time_zone: 'Asia/Jakarta'
    };

  const currentDuration =
    calculateDuration(
      formData.start_time,
      formData.end_time
    );

  const isDateDisabled = (
    dateString
  ) => {
    if (validDates.length === 0) {
      return false;
    }

    return !validDates.includes(
      dateString
    );
  };

  return (
    <div
      className={`absolute top-0 right-0 bottom-0 bg-white shadow-xl flex flex-col z-40 border-l border-gray-200 transition-all duration-300 max-w-[100vw] ${
        isCollapsed
          ? 'w-0 sm:w-[50px]'
          : 'w-[calc(100%-30px)] sm:w-96'
      }`}
    >
      <button
        onClick={onToggleCollapse}
        className={`absolute top-4 bg-white border border-gray-200 rounded-l-md p-2 hover:bg-gray-50 z-50 shadow-md transition-all duration-300 ${
          isCollapsed
            ? 'right-0 sm:left-0 sm:-translate-x-full'
            : 'left-0 -translate-x-full'
        }`}
        aria-label={
          isCollapsed
            ? 'Expand panel'
            : 'Collapse panel'
        }
      >
        {isCollapsed ? (
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-600" />
        )}
      </button>

      {!isCollapsed && (
        <>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2
              className="text-lg font-semibold text-gray-900"
              style={{
                fontFamily: 'Inter'
              }}
            >
              {booking
                ? 'Edit Booking'
                : 'Create Booking'}
            </h2>

            <div className="flex items-center gap-2">
              {booking && (
                <button
                  onClick={() =>
                    onNewBooking &&
                    onNewBooking()
                  }
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="New booking"
                  title="Create new booking"
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700 flex items-start gap-2">
                <svg
                  className="w-4 h-4 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>

                <span>
                  {booking
                    ? 'Booking updated successfully!'
                    : 'Booking created successfully!'}
                </span>
              </div>
            )}

            {booking &&
              bookingIsPast && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />

                  <span>
                    This is a past
                    booking and
                    cannot be edited.
                  </span>
                </div>
              )}

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="relative">
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{
                    fontFamily: 'Inter'
                  }}
                >
                  Customer
                </label>

                <button
                  type="button"
                  onClick={() =>
                    setIsCustomerDropdownOpen(
                      !isCustomerDropdownOpen
                    )
                  }
                  className="w-full text-left truncate border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{
                    fontFamily: 'Inter'
                  }}
                >
                  {formData.customer_id
                    ? customers.find(
                        (c) =>
                          c.id ===
                          formData.customer_id
                      )?.email
                      ? `${formData.customer_name} (${customers.find(
                          (c) =>
                            c.id ===
                            formData.customer_id
                        )?.email})`
                      : formData.customer_name
                    : 'Select Customer'}
                </button>

                {isCustomerDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() =>
                        setIsCustomerDropdownOpen(
                          false
                        )
                      }
                    />

                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col max-h-[75vh]">
                      <div className="p-2 border-b border-gray-200 sticky top-0 bg-white rounded-t-md z-10">
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={
                            customerSearchQuery
                          }
                          onChange={(e) =>
                            setCustomerSearchQuery(
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                          style={{
                            fontFamily:
                              'Inter'
                          }}
                          autoFocus
                        />
                      </div>

                      <div className="overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(
                              (
                                prev
                              ) => ({
                                ...prev,
                                customer_id:
                                  '',
                                customer_name:
                                  ''
                              })
                            );

                            setIsCustomerDropdownOpen(
                              false
                            );

                            setCustomerSearchQuery(
                              ''
                            );
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100"
                        >
                          Select Customer
                          (Clear)
                        </button>

                        {customers
                          .filter(
                            (c) => {
                              const searchStr =
                                customerSearchQuery.toLowerCase();

                              return (
                                (
                                  c.name ||
                                  ''
                                )
                                  .toLowerCase()
                                  .includes(
                                    searchStr
                                  ) ||
                                (
                                  c.email ||
                                  ''
                                )
                                  .toLowerCase()
                                  .includes(
                                    searchStr
                                  )
                              );
                            }
                          )
                          .map(
                            (
                              customer
                            ) => {
                              const displayText =
                                customer.email
                                  ? `${customer.name} (${customer.email})`
                                  : customer.name;

                              return (
                                <button
                                  key={
                                    customer.id
                                  }
                                  type="button"
                                  onClick={() => {
                                    setFormData(
                                      (
                                        prev
                                      ) => ({
                                        ...prev,
                                        customer_id:
                                          customer.id,
                                        customer_name:
                                          customer.name ||
                                          ''
                                      })
                                    );

                                    setIsCustomerDropdownOpen(
                                      false
                                    );

                                    setCustomerSearchQuery(
                                      ''
                                    );
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors truncate ${
                                    formData.customer_id ===
                                    customer.id
                                      ? 'bg-blue-50 text-blue-700 font-medium'
                                      : 'text-gray-700'
                                  }`}
                                  title={
                                    displayText
                                  }
                                >
                                  {
                                    displayText
                                  }
                                </button>
                              );
                            }
                          )}

                        {customers.filter(
                          (c) =>
                            (
                              c.name ||
                              ''
                            )
                              .toLowerCase()
                              .includes(
                                customerSearchQuery.toLowerCase()
                              ) ||
                            (
                              c.email ||
                              ''
                            )
                              .toLowerCase()
                              .includes(
                                customerSearchQuery.toLowerCase()
                              )
                        ).length ===
                          0 && (
                          <div className="px-3 py-4 text-sm text-center text-gray-500">
                            No customers
                            found.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{
                    fontFamily: 'Inter'
                  }}
                >
                  Resource
                </label>

                <select
                  value={
                    formData.resource_id
                  }
                  onChange={(e) =>
                    setFormData(
                      (prev) => ({
                        ...prev,
                        resource_id:
                          e.target.value
                      })
                    )
                  }
                  className="w-full max-w-full truncate border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">
                    Select Resource
                  </option>

                  {resources.map(
                    (resource) => (
                      <option
                        key={
                          resource.id
                        }
                        value={
                          resource.id
                        }
                      >
                        {
                          resource.name
                        }
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{
                    fontFamily: 'Inter'
                  }}
                >
                  Location
                </label>

                <select
                  value={
                    formData.location_id
                  }
                  disabled
                  className="w-full max-w-full truncate border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 cursor-not-allowed"
                  required
                >
                  <option value="80c10e0e-3ead-4740-a2a6-574354bb9a77">
                    Continnum
                  </option>
                </select>
              </div>

              {loadingResourceData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />

                  <span className="text-sm text-blue-800">
                    Loading resource
                    details...
                  </span>
                </div>
              )}

              {formData.price &&
                formData.price !== '0' &&
                !loadingResourceData && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm text-blue-800">
                      <strong>
                        Price:
                      </strong>{' '}
                      IDR{' '}
                      {parseFloat(
                        formData.price
                      ).toLocaleString()}
                    </div>
                  </div>
                )}

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center text-2xl font-bold text-gray-900">
                    {formatTime12Hour(
                      formData.start_time
                    )}
                  </div>

                  <div className="text-gray-400">
                    →
                  </div>

                  <div className="flex items-center text-2xl font-bold text-gray-900">
                    {formatTime12Hour(
                      formData.end_time
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-gray-600">
                    <Globe className="w-4 h-4" />

                    <span>
                      {getTimezoneOffset(
                        selectedLocation?.time_zone ||
                          'Asia/Jakarta'
                      )}{' '}
                      {selectedLocation?.name ||
                        'Jakarta'}
                    </span>
                  </div>

                  <span className="text-gray-500">
                    {formatDuration(
                      currentDuration
                    )}
                  </span>
                </div>
              </div>

              <div className="sm:grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="mb-4 sm:mb-0">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>

                  <input
                    type="date"
                    value={
                      formData.start_date
                    }
                    onChange={(e) =>
                      setFormData(
                        (prev) => ({
                          ...prev,
                          start_date:
                            e.target.value
                        })
                      )
                    }
                    min={
                      isPastBooking
                        ? undefined
                        : new Date()
                            .toISOString()
                            .split(
                              'T'
                            )[0]
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />

                  {!isPastBooking &&
                    validDates.length >
                      0 &&
                    isDateDisabled(
                      formData.start_date
                    ) && (
                      <p className="text-xs text-red-600 mt-1">
                        Not available
                      </p>
                    )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>

                  <input
                    type="date"
                    value={
                      formData.end_date
                    }
                    onChange={(e) =>
                      setFormData(
                        (prev) => ({
                          ...prev,
                          end_date:
                            e.target.value
                        })
                      )
                    }
                    min={
                      formData.start_date
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-2 gap-4">
                <div className="mb-4 sm:mb-0">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4" />
                    Start time *
                    {loadingSlots && (
                      <span className="text-xs text-gray-500">
                        (Loading...)
                      </span>
                    )}
                  </label>

                  <input
                    type="time"
                    value={
                      formData.start_time
                    }
                    onChange={(e) => {
                      const time =
                        e.target.value;

                      setFormData(
                        (prev) => ({
                          ...prev,
                          start_time:
                            time
                        })
                      );

                      if (
                        serviceDetails?.duration &&
                        time
                      ) {
                        setFormData(
                          (prev) => ({
                            ...prev,
                            end_time:
                              addMinutesToTime(
                                time,
                                parseDuration(
                                  serviceDetails.duration
                                )
                              )
                          })
                        );
                      }
                    }}
                    disabled={
                      loadingSlots
                    }
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      loadingSlots
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'bg-white'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4" />
                    End time *
                  </label>

                  <input
                    type="time"
                    value={
                      formData.end_time
                    }
                    onChange={(e) =>
                      setFormData(
                        (prev) => ({
                          ...prev,
                          end_time:
                            e.target.value
                        })
                      )
                    }
                    disabled={
                      loadingSlots
                    }
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      loadingSlots
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'bg-white'
                    }`}
                    required
                  />
                </div>
              </div>

              {availableSlots.length >
                0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="text-sm text-gray-600">
                    {
                      availableSlots.filter(
                        (s) =>
                          s.available &&
                          !isTimePast(
                            formData.start_date,
                            s.time
                          )
                      ).length
                    }{' '}
                    time slots
                    available on
                    this date
                  </div>
                </div>
              )}

              {!booking && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isPastBooking"
                    checked={
                      isPastBooking
                    }
                    onChange={(e) =>
                      setIsPastBooking(
                        e.target.checked
                      )
                    }
                    className="w-4 h-4 border border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-blue-500"
                  />

                  <label
                    htmlFor="isPastBooking"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Past Booking
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note / Remarks
                </label>

                <textarea
                  value={
                    formData.notes
                  }
                  onChange={(e) =>
                    setFormData(
                      (prev) => ({
                        ...prev,
                        notes:
                          e.target.value
                      })
                    )
                  }
                  placeholder="Add any notes or remarks for this booking..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                />
              </div>

              {booking &&
                !bookingIsPast && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Action
                    </h3>

                    <div className="flex gap-3">
                      {!booking.is_temporary ? (
                        <button
                          type="button"
                          onClick={
                            handleMarkTentative
                          }
                          disabled={
                            actionLoading ===
                              'Mark Tentative' ||
                            booking.is_canceled
                          }
                          className="px-4 py-1.5 text-sm font-medium border border-gray-500 text-gray-700 bg-white rounded transition-all hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading ===
                          'Mark Tentative'
                            ? 'Updating...'
                            : 'Mark Tentative'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={
                            handleConfirmBooking
                          }
                          disabled={
                            actionLoading ===
                              'Confirm' ||
                            booking.is_canceled
                          }
                          className="px-4 py-1.5 text-sm font-medium border border-green-600 text-green-600 bg-white rounded transition-all hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading ===
                          'Confirm'
                            ? 'Confirming...'
                            : 'Confirm Booking'}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          setShowCancelConfirmModal(
                            true
                          )
                        }
                        disabled={
                          actionLoading ===
                            'Cancel' ||
                          booking.is_canceled
                        }
                        className="px-4 py-1.5 text-sm font-medium border border-red-500 text-red-500 bg-white rounded transition-all hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoading ===
                        'Cancel'
                          ? 'Canceling...'
                          : 'Cancel Booking'}
                      </button>
                    </div>
                  </div>
                )}
            </form>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            {booking && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={
                  loading || success
                }
                className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                Delete
              </button>
            )}

            <div className="flex-1" />

            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                loading ||
                success ||
                (booking &&
                  bookingIsPast)
              }
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {booking &&
              bookingIsPast
                ? 'Editing disabled'
                : success
                  ? 'Saved!'
                  : loading
                    ? 'Saving...'
                    : 'Save'}
            </button>
          </div>
        </>
      )}

      {showCancelConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Cancel Booking
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you
                want to cancel this
                booking? This action
                cannot be undone.
              </p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={() =>
                    setShowCancelConfirmModal(
                      false
                    )
                  }
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  onClick={
                    handleCancelBooking
                  }
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#EF4444] border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Booking
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you
                want to delete this
                booking? This action
                cannot be undone.
              </p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={() =>
                    setShowDeleteConfirmModal(
                      false
                    )
                  }
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  onClick={
                    confirmDelete
                  }
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? 'Deleting...'
                    : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}